"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WallServiceClient = void 0;
const socket_io_client_1 = require("socket.io-client");
const toolbox_1 = require("@streem/toolbox");
const logger_1 = require("./logger");
const MAX_CONNECT_ATTEMPTS = 3;
const MAX_FETCH_ATTEMPTS_ON_401_ERROR = 2;
class WallServiceClient {
    static configure(wallBaseUrl) {
        // this is different than the api base url.  The typical api base url looks like:
        //    https://api.<env>-us.streem.cloud/, but the wall url typically looks like:
        //    https://wall.<env>-us.streem.cloud/
        this.baseUrl = `${wallBaseUrl}/v1/walls`;
        WallServiceClient.log = (0, logger_1.createLogger)('wall-service-client');
        WallServiceClient.log.info('WallServiceClient configure: baseUrl', this.baseUrl);
    }
    constructor(opts) {
        this.opts = opts;
        (0, toolbox_1.invariant)(WallServiceClient.baseUrl, 'WallServiceClient must be configured first');
        this.token = opts.initialToken;
    }
    connect() {
        this.socket = (0, socket_io_client_1.io)(`${WallServiceClient.baseUrl}/${this.opts.wallId}`, {
            extraHeaders: {
                authorization: `Bearer ${this.token}`,
            },
            reconnection: false,
            autoConnect: false,
            // this allows sticky sessions to work by sending the AWS cookie to the wall service
            withCredentials: true,
        });
        this.socket.on('disconnect', this.handleDisconnect.bind(this));
        this.socket.on('wall_item_entry_event', this.opts.itemEntryEventHandler);
        return this.connectWithRetry();
    }
    disconnect() {
        var _a;
        if ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) {
            this.socket.disconnect();
        }
    }
    // get all of the entries for a wall.  Updates to wall entries will be sent via socket and can
    //   be handled by the handler passed to connect().
    getEntriesForWall() {
        return __awaiter(this, void 0, void 0, function* () {
            // NOTE: based on a note in the wall service (services/wall/src/http/init.ts), it looks like
            //       the entries endpoint may be paginated in the future, but it is not currently.
            const response = yield this.fetchWithRetry(() => fetch(`${WallServiceClient.baseUrl}/${this.opts.wallId}/entries`, {
                credentials: 'include',
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${this.token}`,
                },
                mode: 'cors',
            }));
            if (!response.ok) {
                throw new Error(`failed to get entries for wall: ${this.opts.wallId}: ${response.status}`);
            }
            const { wall_item_entries: wallItemEntries } = yield response.json();
            return wallItemEntries;
        });
    }
    addOrUpdate(value, wallItemEntryId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { wallId } = this.opts;
            const body = {
                wall_item_encoded: value,
                connection_id: this.opts.connectionId,
            };
            const response = yield this.fetchWithRetry(() => fetch(`${WallServiceClient.baseUrl}/${wallId}/entries/${wallItemEntryId}`, {
                body: JSON.stringify(body),
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.token}`,
                },
                method: 'PATCH',
                mode: 'cors',
            }));
            if (!response.ok) {
                throw new Error(`failed to add or update wall item: ${response.status}, wallId=${wallId}, wallItemEntryId=${wallItemEntryId}`);
            }
            return response.json();
        });
    }
    delete(itemId) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                connection_id: this.opts.connectionId,
            };
            const response = yield this.fetchWithRetry(() => fetch(`${WallServiceClient.baseUrl}/${this.opts.wallId}/entries/${itemId}`, {
                body: JSON.stringify(body),
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${this.token}`,
                    'Content-Type': 'application/json',
                },
                method: 'DELETE',
                mode: 'cors',
            }));
            if (!response.ok) {
                throw new Error(`failed to delete wall item: ${response.status}, itemId=${itemId}`);
            }
        });
    }
    clear() {
        this.disconnect();
    }
    handleDisconnect(reason) {
        return __awaiter(this, void 0, void 0, function* () {
            const { wallId } = this.opts;
            if (this.intentionalDisconnect(reason)) {
                // this is a normal disconnect
                WallServiceClient.log.info('web socket disconnected from wall by client', { wallId });
                return;
            }
            WallServiceClient.log.info('unexpected disconnect from wall.  Attempting to reconnect.', {
                reason,
                wallId,
            });
            // since this is an unexpected disconnect, try reconnecting with a new bearer token
            yield this.refreshToken();
            const connected = yield this.connect();
            yield this.opts.reconnectionHandler(connected);
        });
    }
    connectWithRetry(currentAttempt = 1) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const { wallId, connectionErrorHandler } = this.opts;
            if (currentAttempt > MAX_CONNECT_ATTEMPTS) {
                // `streem_wall_sync.handleConnectionError` logs this at the error level
                WallServiceClient.log.info(`max attempts exceeded trying to establish web socket connection to wall`, {
                    wallId,
                });
                connectionErrorHandler(new Error(`max attempts exceeded trying to establish web socket connection to wall: ${wallId}`));
                return false;
            }
            const connected = (_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) !== null && _b !== void 0 ? _b : false;
            (0, toolbox_1.invariant)(!connected, `already connected to wall=${wallId}.  Must disconnect first`);
            WallServiceClient.log.info(`attempt ${currentAttempt} to connect to wall socket`, {
                wallId,
            });
            const connectedToSocket = yield this.createAndConnectToSocket();
            // if socket is successfully connected, return true
            if (connectedToSocket) {
                return connectedToSocket;
            }
            // if socket is not connected, fetch a new token and try again
            yield this.refreshToken();
            return this.connectWithRetry(currentAttempt + 1);
        });
    }
    createAndConnectToSocket() {
        const { wallId } = this.opts;
        return new Promise(resolve => {
            // remove any prior connect and connect_error listeners
            this.socket.off('connect');
            this.socket.off('connect_error');
            this.socket.on('connect', () => {
                WallServiceClient.log.info('connected to wall using socket', { wallId });
                resolve(true);
            });
            this.socket.on('connect_error', (err) => {
                WallServiceClient.log.info('websocket connection error, retrying', {
                    errMessage: err.message,
                    stack: err.stack,
                    wallId,
                });
                resolve(false);
            });
            this.socket.connect();
        });
    }
    fetchWithRetry(fetchFn, attempt = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetchFn();
            if (response.status === 401 && attempt < MAX_FETCH_ATTEMPTS_ON_401_ERROR) {
                yield this.refreshToken();
                return this.fetchWithRetry(fetchFn, attempt + 1);
            }
            return response;
        });
    }
    refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const { token } = yield this.opts.refreshToken();
            this.token = token;
            // update the authorization header with the current token if the socket is connected
            if (this.socket) {
                this.socket.io.opts.extraHeaders.Authorization = `Bearer ${this.token}`;
            }
        });
    }
    intentionalDisconnect(disconnectReason) {
        return disconnectReason === 'io client disconnect';
    }
}
exports.WallServiceClient = WallServiceClient;
//# sourceMappingURL=wall_service_client.js.map